import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_URLS } from '../config/api-urls';
import { API_ENDPOINTS } from '../config/api-endpoints';

export interface BarRegistrationRequest {
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPassword: string;
  ownerPhone?: string;
  barName: string;
  description?: string;
  licenseNumber: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  phone?: string;
  contactEmail?: string;
  website?: string;
  latitude?: number;
  longitude?: number;
  bankAccountNumber?: string;
  mobileMoneyAccount?: string;
}

export interface StaffInviteRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
  role: string;
}

export interface ChangePasswordRequest {
  email: string;
  oldPassword: string;
  newPassword: string;
}

export interface JwtClaims {
  sub?: string;
  user_id?: string;
  user_type?: string;
  bar_id?: string;
  role?: string;
  first_name?: string;
  last_name?: string;
  exp?: number;
  [k: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {

  constructor(private http: HttpClient) {}

  // Cached bearer token + parsed claims, shared with anything that needs
  // JWT-derived identity (livechat needs user_id + email). The AuthInterceptor
  // has its own cache for header injection — this one exists so components
  // can read claims without doing their own /api/token round-trip.
  private cachedToken: string | null = null;
  private cachedClaims: JwtClaims | null = null;
  private tokenFetchInFlight: Promise<string | null> | null = null;

  /** Force refetch on next access — call after logout or role change. */
  clearTokenCache(): void {
    this.cachedToken = null;
    this.cachedClaims = null;
  }

  /** Fetch (and cache) the current session's access token, or null if none. */
  async getAccessToken(): Promise<string | null> {
    if (this.cachedToken) return this.cachedToken;
    if (this.tokenFetchInFlight) return this.tokenFetchInFlight;
    this.tokenFetchInFlight = fetch(`${API_URLS.GATEWAY}/api/token`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(body => {
        this.cachedToken = body?.access_token ?? null;
        this.cachedClaims = this.cachedToken ? this.decodeJwt(this.cachedToken) : null;
        this.tokenFetchInFlight = null;
        return this.cachedToken;
      })
      .catch(() => { this.tokenFetchInFlight = null; return null; });
    return this.tokenFetchInFlight;
  }

  async getClaims(): Promise<JwtClaims | null> {
    if (this.cachedClaims) return this.cachedClaims;
    await this.getAccessToken();
    return this.cachedClaims;
  }

  async getCurrentUserId(): Promise<number | null> {
    const c = await this.getClaims();
    const raw = c?.user_id;
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  async getCurrentUserEmail(): Promise<string | null> {
    const c = await this.getClaims();
    return c?.sub ?? null;
  }

  async getCurrentBarId(): Promise<string | null> {
    const c = await this.getClaims();
    return c?.bar_id ?? null;
  }

  async getCurrentUserType(): Promise<string | null> {
    const c = await this.getClaims();
    return c?.user_type ?? null;
  }

  /** Decode a JWT payload without verifying — verification happens server-side. */
  private decodeJwt(token: string): JwtClaims | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      // base64url → base64
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '==='.slice((b64.length + 3) % 4);
      const json = atob(padded);
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  /**
   * Heuristic session check: Spring's CookieCsrfTokenRepository sets a non-HttpOnly
   * XSRF-TOKEN cookie for authenticated sessions. JSESSIONID is HttpOnly and can't
   * be read from JS, so XSRF-TOKEN is the only reliable client-side "is logged in"
   * signal available to us.
   */
  /**
   * Synchronous, best-effort: true once a session token has been fetched.
   *
   * This used to test for the XSRF-TOKEN cookie, which is not a login signal at
   * all. Spring issues that cookie to every visitor, so the answer was wrong in
   * both directions depending on when the cookie happened to be written — the
   * header offered Login to signed-in users, and hid it from anonymous ones.
   * Prefer isAuthenticatedAsync() when the answer must be authoritative.
   */
  isAuthenticated(): boolean {
    return !!this.cachedToken;
  }

  /** Authoritative: asks the gateway whether this browser has a live session. */
  async isAuthenticatedAsync(): Promise<boolean> {
    return !!(await this.getAccessToken());
  }

  registerCustomer(data: any): Observable<any> {
    return this.http.post(`${API_URLS.GATEWAY}/register`, data);
  }

  registerBar(formData: FormData): Observable<any> {
    return this.http.post(`${API_URLS.GATEWAY}/bars/register`, formData);
  }

  logout(): void {
    window.location.href = `${API_URLS.GATEWAY}/auth/logout`;
  }

  initiateOAuthLogin(): void {
    // Use Spring Security's built-in OAuth2 client initiation endpoint on the
    // gateway. Spring's OAuth2AuthorizationRequestRedirectFilter intercepts this
    // path and issues the correct 302 to the auth-server with all params baked
    // in from spring.security.oauth2.client.registration.api-client-oidc.*
    window.location.href = `${API_URLS.GATEWAY}/oauth2/authorization/api-client-oidc`;
  }

  verifyEmail(token: string): Observable<any> {
    return this.http.get(`${API_URLS.GATEWAY}/verifyRegistration`, { params: { token } });
  }

  resendVerification(token: string): Observable<any> {
    return this.http.get(`${API_URLS.GATEWAY}/resendVerification`, { params: { token } });
  }

  initiatePasswordReset(email: string): Observable<any> {
    return this.http.post(`${API_URLS.GATEWAY}/resetPassword`, { email });
  }

  saveNewPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${API_URLS.GATEWAY}/savePassword`, { newPassword }, { params: { token } });
  }

  changePassword(data: ChangePasswordRequest): Observable<any> {
    return this.http.post(`${API_URLS.GATEWAY}/changePassword`, data);
  }

  // NOTE on paths. API_URLS.GATEWAY is already ".../api".
  //
  //   normal services  ->  "${GATEWAY}/{service}/..."  which the gateway
  //                        rewrites to /api/v1/{service}/...
  //   auth server      ->  an exception: /bars is served directly, so
  //                        "${GATEWAY}/bars". Writing "${GATEWAY}/api/bars"
  //                        asked for /api/api/bars, matched no route, and came
  //                        back as a bare 500 "Internal server error" on every
  //                        load of the provider dashboard.
  //   auth client      ->  serves a few endpoints of its OWN under /api, so
  //                        "${GATEWAY}/api/token" is correct as written.
  //   admin service    ->  does NOT follow the rule above, and is a separate
  //                        Thymeleaf app rather than a JSON API behind the
  //                        gateway. The getPending/Approve/Reject calls below
  //                        are left as their author wrote them; no controller
  //                        serves them today, so they answer 500 either way.
  getBars(): Observable<any[]> {
    return this.http.get<any[]>(`${API_URLS.GATEWAY}/bars`);
  }

  getBar(barId: string): Observable<any> {
    return this.http.get<any>(`${API_URLS.GATEWAY}/bars/${barId}`);
  }

  getBarStaff(barId: string): Observable<any[]> {
    return this.http.get<any[]>(`${API_URLS.GATEWAY}/bars/${barId}/staff`);
  }

  inviteStaff(barId: string, staffData: StaffInviteRequest): Observable<any> {
    return this.http.post<any>(`${API_URLS.GATEWAY}/bars/${barId}/staff`, staffData);
  }

  getPendingBars(): Observable<any[]> {
    return this.http.get<any[]>(`${API_URLS.GATEWAY}/api/admin/bars/pending`);
  }

  getRejectedBars(): Observable<any[]> {
    return this.http.get<any[]>(`${API_URLS.GATEWAY}/api/admin/bars/rejected`);
  }

  getApprovedBars(): Observable<any[]> {
    return this.http.get<any[]>(`${API_URLS.GATEWAY}/api/admin/bars/approved`);
  }

  getBarDetails(barId: string): Observable<any> {
    return this.http.get<any>(`${API_URLS.GATEWAY}/api/admin/bars/${barId}`);
  }

  approveBar(barId: string): Observable<any> {
    return this.http.put<any>(`${API_URLS.GATEWAY}/api/admin/bars/${barId}/approve`, null);
  }

  rejectBar(barId: string, reason: string): Observable<any> {
    return this.http.put<any>(`${API_URLS.GATEWAY}/api/admin/bars/${barId}/reject`, { reason });
  }

  searchBars(filters: { city?: string; country?: string; name?: string; sortBy?: string }): Observable<any[]> {
    let params = new HttpParams();
    if (filters.city) params = params.set('city', filters.city);
    if (filters.country) params = params.set('country', filters.country);
    if (filters.name) params = params.set('name', filters.name);
    if (filters.sortBy) params = params.set('sortBy', filters.sortBy);
    return this.http.get<any[]>(`${API_URLS.GATEWAY}${API_ENDPOINTS.BAR_DISCOVERY.SEARCH}`, { params });
  }

  getNearbyBars(lat: number, lng: number, radiusKm: number = 10): Observable<any[]> {
    return this.http.get<any[]>(`${API_URLS.GATEWAY}${API_ENDPOINTS.BAR_DISCOVERY.NEARBY}`, {
      params: { lat: lat.toString(), lng: lng.toString(), radiusKm: radiusKm.toString() }
    });
  }

  rateBar(barId: string, rating: number): Observable<any> {
    return this.http.put<any>(`${API_URLS.GATEWAY}${API_ENDPOINTS.BAR_DISCOVERY.RATE(barId)}`, null, {
      params: { rating: rating.toString() }
    });
  }

  getCurrentUser(): Observable<any> {
    // The gateway doesn't expose /user. /api/loggedinHello returns the
    // authenticated principal name (200) or 401 when unauthenticated —
    // that binary signal is all the authGuard needs.
    return this.http.get(`${API_URLS.GATEWAY}/api/loggedinHello`, {
      withCredentials: true,
      responseType: 'text'
    });
  }
}
